import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Square,
  GraduationCap,
  Video,
  BookOpen,
  Users,
  BellRing,
  History,
  Network,
} from "lucide-react";

const BG_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/374c21d4-89bd-4744-b399-aad8dd924688/images/b625c87b2138bb7180af83c9be1af9f8a289d66149ee583adfe762fe69f82de2.png";

// Benefits available to anyone with an account, including free guests.
const FREE_BENEFITS = [
  {
    icon: Video,
    title: "On-demand training videos",
    desc: "Catch up at your own pace — every session is recorded and indexed.",
  },
  {
    icon: BookOpen,
    title: "Companion eBook",
    desc: "A reference manual covering the recursive Bayes framework end to end.",
  },
  {
    icon: History,
    title: "Five years of full trading history",
    desc: "Browse every trade and yearly P/L for the last 5 years — even on a free guest account.",
  },
];

// Benefits reserved for paying full members.
const MEMBER_BENEFITS = [
  {
    icon: Network,
    title: "The Bayesian network model",
    desc: "Live access to our model that scores which stocks are good and tells you when to trade them.",
  },
  {
    icon: GraduationCap,
    title: "Live training sessions via Zoom",
    desc: "Learn the Bayesian options playbook from the team that built it.",
  },
  {
    icon: Users,
    title: "Monthly Zoom session after expiration day",
    desc: "Trade together, review positions, and go through the watchlist and market conditions as a group.",
  },
  {
    icon: BellRing,
    title: "Trade alerts via Discord & WhatsApp",
    desc: "Real-time pings the moment we open, adjust, or close a position.",
  },
];

function BenefitItem({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="shrink-0 w-9 h-9 rounded-md bg-white/70 border border-slate-200 flex items-center justify-center shadow-sm">
        <Icon className="w-4 h-4 text-slate-700" strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900 leading-snug">
          {title}
        </div>
        <div className="text-xs text-slate-600 mt-1 leading-relaxed">
          {desc}
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to="/stocks-in-play" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result =
      mode === "login"
        ? await login(email, password)
        : await register(email, password, name);
    setLoading(false);
    if (!result.ok) setError(result.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* Visual + benefits side (desktop) */}
      <div className="hidden lg:block relative overflow-hidden bg-slate-50">
        <img src={BG_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/75 to-white/85" />
        <div className="relative h-full flex flex-col p-12 overflow-y-auto">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-9 h-9 bg-slate-900 flex items-center justify-center rounded-sm">
              <Square className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-heading font-black tracking-tighter text-2xl text-slate-900">
              BAYESIAN
            </div>
          </div>

          <div className="max-w-md mb-10">
            <div className="text-xs font-medium tracking-[0.25em] text-slate-500 uppercase mb-3">
              Options
            </div>
            <h2 className="text-4xl font-black tracking-tighter text-slate-900 font-heading leading-[1.05]">
              Your team's daily options playbook.
            </h2>
            <p className="text-slate-600 mt-4 text-sm leading-relaxed">
              Members curate the real plays. Guests can browse the platform with
              anonymised tickers — upgrade any time.
            </p>
          </div>

          <div className="max-w-md">
            <div data-testid="login-benefits">
              <div className="flex items-center gap-2 mb-5">
                <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-emerald-700">
                  Free with any account
                </span>
                <span className="flex-1 h-px bg-emerald-200" />
              </div>
              <div className="space-y-5 mb-10">
                {FREE_BENEFITS.map((b) => (
                  <BenefitItem key={b.title} {...b} />
                ))}
              </div>

              <div className="flex items-center gap-2 mb-5">
                <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-slate-700">
                  Full membership adds
                </span>
                <span className="flex-1 h-px bg-slate-200" />
              </div>
              <div className="space-y-5">
                {MEMBER_BENEFITS.map((b) => (
                  <BenefitItem key={b.title} {...b} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center px-6 sm:px-12 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-slate-900 flex items-center justify-center rounded-sm">
              <Square className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-heading font-black tracking-tighter text-xl text-slate-900">
              BAYESIAN
            </div>
          </div>

          {/* Mobile-only condensed benefits */}
          <div className="lg:hidden mb-8 rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-5">
            <div>
              <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-emerald-700 mb-3">
                Free with any account
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                {FREE_BENEFITS.map(({ icon: Icon, title }) => (
                  <li key={title} className="flex items-start gap-2">
                    <Icon
                      className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0"
                      strokeWidth={1.75}
                    />
                    <span className="leading-snug">{title}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-slate-700 mb-3">
                Full membership adds
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                {MEMBER_BENEFITS.map(({ icon: Icon, title }) => (
                  <li key={title} className="flex items-start gap-2">
                    <Icon
                      className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0"
                      strokeWidth={1.75}
                    />
                    <span className="leading-snug">{title}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase mb-2">
              Welcome
            </div>
            <h1 className="text-3xl font-black tracking-tighter text-slate-900 font-heading">
              {mode === "login" ? "Sign in to your desk" : "Create a guest account"}
            </h1>
          </div>

          <Tabs value={mode} onValueChange={setMode} className="mb-6">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Sign up</TabsTrigger>
            </TabsList>
            <TabsContent value="login" />
            <TabsContent value="register" />
          </Tabs>

          {mode === "register" && (
            <div
              className="mb-5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600 leading-relaxed"
              data-testid="guest-notice"
            >
              You're creating a <span className="font-semibold text-slate-900">Guest account</span>. Guests can browse with anonymised tickers. Full member access requires upgrading via "Our Offer".
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs tracking-[0.15em] uppercase text-slate-600">
                  Name
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5"
                  placeholder="Jane Doe"
                  data-testid="name-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs tracking-[0.15em] uppercase text-slate-600">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                placeholder="you@example.com"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-[0.15em] uppercase text-slate-600">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                placeholder="••••••••"
                data-testid="password-input"
              />
            </div>

            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="auth-submit-button"
            >
              {loading
                ? "Please wait..."
                : mode === "login"
                ? "Sign in"
                : "Create guest account"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
